'use client';

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { Loader2, Search } from 'lucide-react';

/**
 * Общее у сетевых инструментов: строка ввода, запрос к `/api/set/*`,
 * ошибки словами и таблица «поле — значение».
 *
 * Запрос живёт в адресе (`?q=example.com`): результат можно переслать, а
 * открытая по ссылке страница сразу показывает ответ.
 */

export interface OshibkaApi {
  code: string;
  ssylka?: string;
}

export type Sostoyanie<T> =
  | { vid: 'pusto' }
  | { vid: 'zhdyom' }
  | { vid: 'gotovo'; dannye: T }
  | { vid: 'oshibka'; oshibka: OshibkaApi };

export async function zaprosit<T>(instrument: string, q: string): Promise<Sostoyanie<T>> {
  try {
    const r = await fetch(`/api/set/${instrument}?${new URLSearchParams({ q }).toString()}`, {
      cache: 'no-store',
    });
    const j = (await r.json()) as T | { error: OshibkaApi };
    if (!r.ok || (j && typeof j === 'object' && 'error' in j)) {
      return {
        vid: 'oshibka',
        oshibka: (j as { error?: OshibkaApi }).error ?? { code: 'upstream' },
      };
    }
    return { vid: 'gotovo', dannye: j };
  } catch {
    return { vid: 'oshibka', oshibka: { code: 'network' } };
  }
}

/** Запрос из адреса страницы и запись его обратно — без перезагрузки. */
export function useZaprosVAdrese(): [string, (q: string) => void] {
  const [q, setQ] = useState('');
  useEffect(() => {
    setQ(new URLSearchParams(window.location.search).get('q') ?? '');
  }, []);
  const zapomnit = useCallback((novyj: string) => {
    const u = new URL(window.location.href);
    if (novyj) u.searchParams.set('q', novyj);
    else u.searchParams.delete('q');
    window.history.replaceState(null, '', u.toString());
    setQ(novyj);
  }, []);
  return [q, zapomnit];
}

/** Инструмент «введи — получи ответ» целиком: форма, ожидание, ошибка. */
// T связывает ответ API с тем, кто его рисует (`pokazat`): без параметра
// каждый инструмент получал бы unknown и приводил тип сам.
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
export function Instrument<T>({
  instrument,
  placeholder,
  primery,
  pokazat,
}: {
  instrument: string;
  placeholder: string;
  /** Готовые примеры под полем — чтобы попробовать в один клик. */
  primery: string[];
  pokazat: (d: T) => ReactNode;
}) {
  const t = useTranslations('dh_set_ui');
  const [q, setQ] = useZaprosVAdrese();
  const [vvod, setVvod] = useState('');
  const [s, setS] = useState<Sostoyanie<T>>({ vid: 'pusto' });
  const poslednij = useRef('');

  const zapustit = useCallback(
    async (zapros: string) => {
      const z = zapros.trim();
      if (!z) return;
      poslednij.current = z;
      setQ(z);
      setS({ vid: 'zhdyom' });
      const otvet = await zaprosit<T>(instrument, z);
      // Ответ на устаревший запрос не затирает свежий.
      if (poslednij.current === z) setS(otvet);
    },
    [instrument, setQ],
  );

  useEffect(() => {
    if (q && !poslednij.current) {
      setVvod(q);
      void zapustit(q);
    }
  }, [q, zapustit]);

  return (
    <div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void zapustit(vvod);
        }}
        className="flex flex-col gap-3 sm:flex-row"
      >
        <label className="relative flex-1">
          <span className="sr-only">{placeholder}</span>
          <Search
            aria-hidden
            className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[var(--color-text-muted)]"
          />
          <input
            value={vvod}
            onChange={(e) => {
              setVvod(e.target.value);
            }}
            placeholder={placeholder}
            maxLength={300}
            spellCheck={false}
            autoCapitalize="off"
            autoCorrect="off"
            className="h-12 w-full rounded-full border border-[var(--color-border)] bg-[var(--color-bg-card)] pl-12 pr-5 font-mono text-[15px] outline-none transition-colors placeholder:font-sans placeholder:text-[var(--color-text-muted)] focus:border-[var(--color-accent-500)]"
          />
        </label>
        <button
          type="submit"
          disabled={s.vid === 'zhdyom'}
          className="dh-cta inline-flex h-12 items-center justify-center gap-2 rounded-full px-8 font-medium transition-colors disabled:opacity-60"
        >
          {s.vid === 'zhdyom' ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
          {t('proverit')}
        </button>
      </form>
      <p className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-sm text-[var(--color-text-muted)]">
        {t('naprimer')}
        {primery.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => {
              setVvod(p);
              void zapustit(p);
            }}
            className="font-mono underline-offset-2 hover:text-[var(--color-text)] hover:underline"
          >
            {p}
          </button>
        ))}
      </p>

      <div className="mt-6" aria-live="polite">
        {s.vid === 'gotovo' ? pokazat(s.dannye) : null}
        {s.vid === 'oshibka' ? <Oshibka o={s.oshibka} /> : null}
      </div>
    </div>
  );
}

const KODY = new Set([
  'bad_input',
  'private_address',
  'no_address',
  'rate_limited',
  'no_whois',
  'no_https',
  'not_found',
  'no_ip',
  'network',
]);

export function Oshibka({ o }: { o: OshibkaApi }) {
  const t = useTranslations('dh_set_ui');
  return (
    <div className="rounded-2xl border border-[var(--color-warning-500)]/40 p-5 text-sm">
      <p>{t(`oshibka_${KODY.has(o.code) ? o.code : 'upstream'}`)}</p>
      {o.ssylka ? (
        <a
          href={o.ssylka}
          target="_blank"
          rel="noopener nofollow"
          className="mt-2 inline-block underline underline-offset-2"
        >
          {new URL(o.ssylka).hostname}
        </a>
      ) : null}
    </div>
  );
}

/** Таблица «поле — значение». Пустые значения не рисуются. */
export function Polya({ stroki }: { stroki: [string, ReactNode][] }) {
  const vidimye = stroki.filter(([, v]) => v !== undefined && v !== null && v !== '');
  return (
    <dl className="divide-y divide-[var(--color-border)] rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg-card)]">
      {vidimye.map(([k, v]) => (
        <div key={k} className="grid gap-1 px-5 py-3 sm:grid-cols-[180px_1fr] sm:gap-4">
          <dt className="text-sm text-[var(--color-text-muted)]">{k}</dt>
          <dd className="min-w-0 text-sm [overflow-wrap:anywhere]">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Дата из ответа: ISO — по-человечески, прочее (WHOIS бывает любым) — как есть. */
export function useData() {
  const locale = useLocale();
  return useCallback(
    (v: string | undefined) => {
      if (!v) return '';
      const d = new Date(v);
      return Number.isNaN(d.getTime()) || !/^\d{4}-\d\d-\d\d/.test(v)
        ? v
        : new Intl.DateTimeFormat(locale, { dateStyle: 'long', timeZone: 'UTC' }).format(d);
    },
    [locale],
  );
}

/** Страна по коду — на языке страницы: DE → «Германия». */
export function useStrana() {
  const locale = useLocale();
  return useCallback(
    (kod: string | undefined) => {
      if (!kod || !/^[A-Z]{2}$/i.test(kod)) return '';
      try {
        const imya = new Intl.DisplayNames([locale], { type: 'region' }).of(kod.toUpperCase());
        return imya && imya !== kod.toUpperCase()
          ? `${imya} (${kod.toUpperCase()})`
          : kod.toUpperCase();
      } catch {
        return kod.toUpperCase();
      }
    },
    [locale],
  );
}
